/** PP invitations are bearer links. Keep them in this tab only, never in localStorage. */
export interface AcademyCredentials { source: string; token: string }
export interface AcademyAccess {
  sessionId: number;
  sessionTitle: string;
  roomSlug: string;
  displayName: string;
  memberNumber: string;
  email: string;
  startsAt: string;
  endsAt: string;
  isHost: boolean;
  canJoin: boolean;
  livekitUrl: string;
  token?: string;
  identity?: string;
}

const STORAGE_KEY = 'vorbello_pp_invitation_v1';
const TRUSTED_SITES = new Set(['puterea-poporului.ro', 'www.puterea-poporului.ro']);
let credentials: AcademyCredentials | null = null;
let details: AcademyAccess | null = null;

export function parseAcademyInvitation(input: string): AcademyCredentials | null {
  let url: URL;
  try { url = new URL(input); } catch { return null; }
  const params = new URLSearchParams(url.hash.replace(/^#/, ''));
  if (!params.has('mpd_invite') && !params.has('mpd_source')) return null;
  if (url.protocol !== 'https:' || url.hostname !== 'app.vorbello.eu') {
    throw new Error('Invitația trebuie deschisă din aplicația oficială app.vorbello.eu.');
  }
  const source = new URL(params.get('mpd_source') || '');
  const route = source.searchParams.get('rest_route') || source.pathname;
  if (source.protocol !== 'https:' || !TRUSTED_SITES.has(source.hostname)
      || source.username || source.password || (source.port && source.port !== '443')
      || !route.endsWith('/mpd/v1/academy-vorbello/invitation')) {
    throw new Error('Această invitație nu este emisă de site-ul PP.');
  }
  const token = params.get('mpd_invite') || '';
  if (!/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(token) || token.length > 8000) {
    throw new Error('Invitația este incompletă. Copiază linkul complet din email.');
  }
  return { source: source.href, token };
}

export function captureAcademyInvitation(input: string): boolean {
  const parsed = parseAcademyInvitation(input);
  if (!parsed) return false;
  credentials = parsed;
  details = null;
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
  return true;
}

export function restoreAcademyInvitation(): boolean {
  const saved = sessionStorage.getItem(STORAGE_KEY);
  if (!saved) return false;
  try {
    const p = JSON.parse(saved) as AcademyCredentials;
    return captureAcademyInvitation(`https://app.vorbello.eu/#${new URLSearchParams({mpd_source: p.source, mpd_invite: p.token})}`);
  } catch { sessionStorage.removeItem(STORAGE_KEY); return false; }
}

export function hasAcademyInvitation(): boolean { return credentials !== null; }
export function getAcademyAccess(): AcademyAccess | null { return details; }

export async function loadAcademyInvitation(issueToken = false): Promise<AcademyAccess> {
  if (!credentials) throw new Error('Deschide invitația personală primită prin email.');
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch(credentials.source, {
      method: 'POST', credentials: 'omit', cache: 'no-store', referrerPolicy: 'no-referrer',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: credentials.token, issueToken }), signal: controller.signal,
    });
    const json = await response.json();
    if (!response.ok || json.success !== true) throw new Error(json.message || 'Site-ul PP nu a putut valida invitația.');
    const d = json.data as AcademyAccess;
    if (!d || !Number.isInteger(d.sessionId) || !/^[a-z0-9][a-z0-9_-]{1,119}$/.test(d.roomSlug)
        || typeof d.displayName !== 'string' || d.displayName.length > 250
        || !/^wss:\/\/[a-z0-9.-]+(?::443)?\/?$/i.test(d.livekitUrl)
        || typeof d.isHost !== 'boolean' || (issueToken && (!d.token || !d.identity))) {
      throw new Error('Răspunsul pentru invitație nu este complet. Contactează organizatorul.');
    }
    details = d;
    return d;
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      throw new Error('Site-ul PP nu a răspuns. Verifică internetul și încearcă din nou.');
    }
    throw err;
  } finally { clearTimeout(timer); }
}
